import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FilterMenu } from "@/components/ui/filter-menu";
import { ColumnsMenu } from "@/components/ui/columns-menu";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { TableStateRow } from "@/components/ui/table-state-row";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { nutritionAPI, type FoodSource } from "@/lib/nutrition-api";
import { DraftInProgressPill } from "@/components/settings/nutrition-shared";
import { useToast } from "@/components/ui/use-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useUrlListState } from "@/hooks/use-url-list-state";
import { queryKeys } from "@/lib/query-keys";
import { useDebounce } from "@/hooks/use-debounce";
import { useFoodTypes } from "@/hooks/useNutritionEvaluation";
import { formatNutrientAmount } from "@/lib/nutrition-form-utils";

interface FoodItemNutrientValue {
  energy?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
}

interface FoodItemListItem {
  id: number;
  food_code?: string;
  name: string;
  full_name: string;
  source?: string;
  food_type?: string;
  food_type_display?: string;
  energy_kcal: string;
  alcohol_g: string;
  caffeine_mg: string;
  water_g: string;
  food_item_nutrient_values: FoodItemNutrientValue;
  has_unpublished_changes?: boolean;
  food_version_number?: number | null;
  is_retired?: boolean;
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FoodItemListItem[];
}

// The All/Published/Drafts tabs, the `?has_unpublished_changes` list filter, and
// the row-level draft pill all depend on the FV8 payload additions (unblock
// item 7). Enabled now that FV8 ships in Columbus #410 (deployed to staging).
// If this ever runs against a backend without #410 the filter is ignored and
// every tab lists all foods, so keep it in step with the deployed backend.
const FV8_DRAFT_FILTER_ENABLED = true;

// Food code and name always show; these can be hidden from the Columns menu.
const OPTIONAL_COLUMNS = [
  { key: "type", label: "Type" },
  { key: "source", label: "Source" },
  { key: "energy", label: "Energy" },
  { key: "protein", label: "Protein" },
  { key: "carbs", label: "Carbs" },
  { key: "fat", label: "Fat" },
  { key: "fiber", label: "Fiber" },
] as const;
type OptionalColumn = (typeof OPTIONAL_COLUMNS)[number]["key"];
const NUTRIENT_COLUMNS = [
  { key: "energy", field: "energyKcal" },
  { key: "protein", field: "protein" },
  { key: "carbs", field: "carbs" },
  { key: "fat", field: "fat" },
  { key: "fiber", field: "fiber" },
] as const;

type StatusTab = "all" | "published" | "drafts";

const STATUS_TABS: readonly StatusTab[] = ["all", "published", "drafts"];

export const ViewFoodItems = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // Search, filters, tab and page live in the URL, so coming back from a food
  // lands on the same results instead of the full list.
  const [listState, setListState] = useUrlListState({
    q: "",
    source: [] as string[],
    type: [] as string[],
    tab: "all",
    page: 1,
  });
  const searchInput = listState.q;
  const sourceFilter = listState.source;
  const foodTypeFilter = listState.type;
  const statusTab: StatusTab = STATUS_TABS.includes(listState.tab as StatusTab)
    ? (listState.tab as StatusTab)
    : "all";
  const currentPage = Math.max(listState.page, 1);
  const setSearchInput = (q: string) => setListState({ q });
  const setStatusTab = (tab: StatusTab) => setListState({ tab });
  const setCurrentPage = (page: number) => setListState({ page });

  const debouncedSearch = useDebounce(searchInput, 350);
  const { visible: visibleColumns, toggle: toggleColumn } =
    useColumnVisibility<OptionalColumn>(OPTIONAL_COLUMNS.map((c) => c.key));
  const columnCount = 2 + visibleColumns.size;
  const pageSize = 20;

  // Raw / Recipe / Supplement / … — the backend owns this list.
  const { data: foodTypes = [] } = useFoodTypes();

  const { data: foodSources } = useQuery({
    queryKey: ["nutrition", "foodSources", "list"] as const,
    queryFn: () => nutritionAPI.foodSources.list(),
    staleTime: 5 * 60 * 1000,
  });

  const sourceOptions = useMemo(
    () =>
      ((foodSources as FoodSource[] | undefined) || [])
        .map((s) => s.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [foodSources]
  );
  // The backend takes several of each, comma-separated.
  const sourceParam = sourceFilter.length ? sourceFilter.join(",") : undefined;
  const foodTypeParam = foodTypeFilter.length
    ? foodTypeFilter.join(",")
    : undefined;
  const hasActiveFilters = sourceFilter.length > 0 || foodTypeFilter.length > 0;

  const toggleIn = (value: string) => (current: string[]) =>
    current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
  const toggleSource = (value: string) =>
    setListState({ source: toggleIn(value)(sourceFilter) });
  const toggleFoodType = (value: string) =>
    setListState({ type: toggleIn(value)(foodTypeFilter) });
  const resetFilters = () => {
    setListState({ source: [], type: [] });
  };

  const {
    data: foodItemsData,
    isLoading,
    isFetching,
    isError,
    refetch,
    error: foodItemsError,
  } = useQuery({
    queryKey: queryKeys.nutrition.foodItems.list({
      page: currentPage,
      page_size: pageSize,
      search: debouncedSearch || undefined,
      source: sourceParam,
      food_type: foodTypeParam,
      has_unpublished_changes:
        FV8_DRAFT_FILTER_ENABLED && statusTab !== "all"
          ? statusTab === "drafts"
          : undefined,
    }),
    queryFn: () =>
      nutritionAPI.foodItems.list({
        page: currentPage,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        source: sourceParam,
        food_type: foodTypeParam,
        has_unpublished_changes:
          statusTab === "all" ? undefined : statusTab === "drafts",
      }),
    retry: false,
  });

  useEffect(() => {
    if (foodItemsError) {
      toast({
        title: "Error",
        description:
          (foodItemsError as any)?.message || "Failed to fetch food items",
        variant: "destructive",
      });
    }
  }, [foodItemsError, toast]);

  const foodItems =
    (foodItemsData as PaginatedResponse | undefined)?.results || [];
  const totalCount = foodItemsData?.count || 0;
  const hasNext = !!foodItemsData?.next;
  const hasPrevious = !!foodItemsData?.previous;

  const handleNextPage = () => {
    if (hasNext) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (hasPrevious) setCurrentPage(currentPage - 1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="h-full flex flex-col gap-4">
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="gap-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="z-h2">Food Items</CardTitle>
            <Button
              onClick={() => navigate("/settings/nutrition/add-food-entry")}
            >
              <Plus className="h-4 w-4" />
              Add Food
            </Button>
          </div>

          {FV8_DRAFT_FILTER_ENABLED && (
            <Tabs
              value={statusTab}
              onValueChange={(v) => setStatusTab(v as StatusTab)}
            >
              <TabsList variant="line">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="published">Published</TabsTrigger>
                <TabsTrigger value="drafts">Drafts</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <SearchInput
            placeholder="Search by food code or name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
            isLoading={isFetching}
            containerClassName="w-full md:max-w-md"
          />

          <div className="flex flex-wrap items-center gap-2">
            <FilterMenu
              label="Type"
              options={foodTypes}
              value={foodTypeFilter}
              onValueChange={toggleFoodType}
              multiple
            />
            <FilterMenu
              label="Source"
              options={sourceOptions}
              value={sourceFilter}
              onValueChange={toggleSource}
              multiple
            />
            <div className="ml-auto flex items-center gap-2">
              <ColumnsMenu
                columns={OPTIONAL_COLUMNS}
                visible={visibleColumns}
                onToggle={toggleColumn}
              />
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={resetFilters}>
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col overflow-hidden">
          <div className="mb-3 z-caption text-muted-foreground">
            Showing {foodItems.length} of {totalCount} food items
            {debouncedSearch && ` for "${debouncedSearch}"`}
          </div>

          <div className="border rounded-lg flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="w-[120px] z-eyebrow text-muted-foreground">
                    Food Code
                  </TableHead>
                  <TableHead className="z-eyebrow text-muted-foreground">
                    Food Name
                  </TableHead>
                  {visibleColumns.has("type") && (
                    <TableHead className="w-[120px] z-eyebrow text-muted-foreground">
                      Type
                    </TableHead>
                  )}
                  {visibleColumns.has("source") && (
                    <TableHead className="w-[140px] z-eyebrow text-muted-foreground">
                      Source
                    </TableHead>
                  )}
                  {NUTRIENT_COLUMNS.filter((c) =>
                    visibleColumns.has(c.key)
                  ).map((c) => (
                    <TableHead
                      key={c.key}
                      className="text-right z-eyebrow text-muted-foreground"
                    >
                      {OPTIONAL_COLUMNS.find((o) => o.key === c.key)?.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableStateRow
                    colSpan={columnCount}
                    state="loading"
                    message="Loading food items…"
                  />
                ) : isError ? (
                  <TableStateRow
                    colSpan={columnCount}
                    state="error"
                    message="Couldn't load food items — the server didn't answer in time."
                    onRetry={() => refetch()}
                  />
                ) : foodItems.length === 0 ? (
                  <TableStateRow
                    colSpan={columnCount}
                    state="empty"
                    message="No food items found"
                  />
                ) : (
                  foodItems.map((item) => (
                    <TableRow
                      key={item.id}
                      className="cursor-pointer transition-colors hover:bg-muted/30"
                      onClick={() =>
                        navigate(`/settings/nutrition/view-food/${item.id}`, {
                          state: { foodListSearch: location.search },
                        })
                      }
                    >
                      <TableCell>
                        {item.food_code ? (
                          <Badge
                            variant="neutral"
                            className="font-mono tabular-nums"
                          >
                            {item.food_code}
                          </Badge>
                        ) : (
                          <span className="z-caption text-muted-foreground">
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <div className="min-w-[240px] max-w-[420px]">
                          <div className="z-body flex items-start gap-2">
                            <span className="break-words">{item.name}</span>
                            {FV8_DRAFT_FILTER_ENABLED &&
                              item.has_unpublished_changes && (
                                <DraftInProgressPill compact />
                              )}
                          </div>
                          {item.full_name !== item.name && (
                            <div className="z-caption text-muted-foreground break-words">
                              {item.full_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      {visibleColumns.has("type") && (
                        <TableCell>
                          {item.food_type_display || item.food_type ? (
                            <Badge variant="neutral">
                              {item.food_type_display || item.food_type}
                            </Badge>
                          ) : (
                            <span className="z-caption text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.has("source") && (
                        <TableCell>
                          {item.source ? (
                            <Badge variant="secondary">{item.source}</Badge>
                          ) : (
                            <span className="z-caption text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                      )}
                      {NUTRIENT_COLUMNS.filter((c) =>
                        visibleColumns.has(c.key)
                      ).map((c) => (
                        <TableCell key={c.key} className="text-right z-numeric">
                          {formatNutrientAmount(
                            c.key === "energy"
                              ? item.energy_kcal
                              : item.food_item_nutrient_values[c.key],
                            c.field
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="z-caption text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={!hasPrevious || isLoading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!hasNext || isLoading}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ViewFoodItems;
